import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';

// ── DTOs ──────────────────────────────────────────────────────────────────────

export type PolicyType =
  | 'Access'
  | 'ApprovalLimit'
  | 'Posting'
  | 'Override'
  | 'ReportVisibility'
  | 'Workflow'
  | 'Password'
  | 'Session';

export interface PolicyListDto {
  id:                 string;
  policyCode:         string;
  policyName:         string;
  policyType:         PolicyType | string;
  appliesToRoleName?: string | null;
  appliesToUserName?: string | null;
  transactionType?:   string | null;
  amountLimitFrom?:   number | null;
  amountLimitTo?:     number | null;
  currencyCode?:      string | null;
  effectiveFrom?:     string | null;
  effectiveTo?:       string | null;
  isActive:           boolean;
  createdDate:        string;
}

export interface PolicyDto {
  id:                 string;
  companyId:          string;
  policyCode:         string;
  policyName:         string;
  policyType:         PolicyType | string;
  appliesToRoleId?:   string | null;
  appliesToRoleName?: string | null;
  appliesToUserId?:   string | null;
  appliesToUserName?: string | null;
  locationId?:        string | null;
  locationName?:      string | null;
  costCenterId?:      string | null;
  costCenterName?:    string | null;
  transactionType?:   string | null;
  permissionCode?:    string | null;
  amountLimitFrom?:   number | null;
  amountLimitTo?:     number | null;
  currencyCode?:      string | null;
  effectiveFrom?:     string | null;
  effectiveTo?:       string | null;
  isActive:           boolean;
  remarks?:           string | null;
  createdDate:        string;
  createdByName?:     string | null;
}

export interface PolicyFilterDto {
  searchTerm?:       string;
  policyType?:       string;
  appliesToRoleId?:  string;
  appliesToUserId?:  string;
  isActive?:         boolean;
  pageNumber?:       number;
  pageSize?:         number;
}

export interface CreatePolicyRequest {
  policyCode:        string;
  policyName:        string;
  policyType:        string;
  appliesToRoleId?:  string | null;
  appliesToUserId?:  string | null;
  locationId?:       string | null;
  costCenterId?:     string | null;
  transactionType?:  string | null;
  permissionCode?:   string | null;
  amountLimitFrom?:  number | null;
  amountLimitTo?:    number | null;
  currencyCode?:     string | null;
  effectiveFrom?:    string | null;
  effectiveTo?:      string | null;
  isActive:          boolean;
  remarks?:          string | null;
}

export type UpdatePolicyRequest = CreatePolicyRequest;

// ── Policy labels and colours ─────────────────────────────────────────────────

export const POLICY_TYPE_LABELS: Record<string, string> = {
  Access:             'Access Control',
  ApprovalLimit:      'Approval Limit',
  Posting:            'Posting Rights',
  Override:           'Override Permission',
  ReportVisibility:   'Report Visibility',
  Workflow:           'Workflow Routing',
  Password:           'Password Policy',
  Session:            'Session Policy',
};

export const POLICY_TYPE_COLORS: Record<string, string> = {
  Access:           'bg-blue-100 text-blue-700',
  ApprovalLimit:    'bg-green-100 text-green-700',
  Posting:          'bg-purple-100 text-purple-700',
  Override:         'bg-red-100 text-red-700',
  ReportVisibility: 'bg-amber-100 text-amber-700',
  Workflow:         'bg-indigo-100 text-indigo-700',
  Password:         'bg-rose-100 text-rose-700',
  Session:          'bg-teal-100 text-teal-700',
};

// ── API client ────────────────────────────────────────────────────────────────

export const policyApi = {
  /** GET /api/v1/policies — paginated list */
  getList: (filter?: PolicyFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<PolicyListDto>>>('/policies', { params: filter }),

  /** GET /api/v1/policies/{id} */
  getById: (id: string) =>
    apiClient.get<ApiResponse<PolicyDto>>(`/policies/${id}`),

  /** POST /api/v1/policies */
  create: (data: CreatePolicyRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/policies', data),

  /** PUT /api/v1/policies/{id} */
  update: (id: string, data: UpdatePolicyRequest) =>
    apiClient.put<ApiResponse<object>>(`/policies/${id}`, data),

  /** DELETE /api/v1/policies/{id} */
  delete: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/policies/${id}`),
};
